import React, { useCallback, useEffect, useRef, useState } from "react";
import { StorefrontInfoModal } from "web/react/components/storefront-info-modal/storefront-info-modal";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Button } from "web/react/emo/button";
import { Text } from "web/react/emo/text";
import { useIntersectionObserver } from "web/react/hooks/use-intersection-observer/use-intersection-observer";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { FeedPromotionsSerializer } from "web/types/serializers";
import { PromotionInfo } from "./promotion-info/promotion-info";
import * as styles from "./promotion-summary.css";

export interface PromotionSummaryProps {
    promotions: FeedPromotionsSerializer;
    title: string;
}

export function PromotionSummary({ promotions, title }: PromotionSummaryProps): React.ReactElement {
    const { active_promotions } = promotions;
    const [openModal, setOpenModal] = useState(false);
    const promotionsCouponRef = useRef<HTMLDivElement | null>(null);
    const [isVisible] = useIntersectionObserver(
        {
            rootMargin: "0px",
            threshold: 1,
            once: true,
        },
        promotionsCouponRef
    );

    useEffect(() => {
        if (isVisible) handleImpression?.();
    }, [isVisible]);

    function handleImpression(): void {
        analytics.event(
            "promotion_module_see_all",
            "impression",
            "",
            false,
            {},
            "",
            {},
            {
                retailer_network: title,
            }
        );
    }

    const activePromotionsTitle =
        active_promotions?.length === 1
            ? gettext("promotion_message.promotion_text.single_active_promotion")
            : gettext("promotion_message.promotion_text.active_promotions", {
                  number: active_promotions.length.toString(),
              });

    const handleOpenModal = useCallback(() => {
        setOpenModal(true);
        analytics.event(
            "promotion_module_see_all",
            "clicked",
            "",
            false,
            {},
            "",
            {},
            {
                retailer_network: title,
            }
        );
    }, []);
    const handleCloseModal = useCallback(() => setOpenModal(false), []);

    return (
        <>
            <div className={styles.promotionSummaryContainer} ref={promotionsCouponRef}>
                <div className={styles.promoSummaryTextWrapper}>
                    <SVGIcon name="tag-fill" className={styles.tagIcon} flip />
                    <Text as="p" textStyle="body-s">
                        {activePromotionsTitle}
                    </Text>
                </div>
                <Button
                    title={gettext("promotion.button.see_all")}
                    size={"sm"}
                    variant={"primary"}
                    onClick={handleOpenModal}
                />
            </div>
            <StorefrontInfoModal title={title} isOpen={openModal} onClose={handleCloseModal}>
                <PromotionInfo promotions={promotions} />
            </StorefrontInfoModal>
        </>
    );
}
